import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import UndoIcon from '@mui/icons-material/Undo';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import moment from 'moment';
import 'moment/locale/es';
import { appConstants } from '../_constants/app.constants';
import {
  applyBracketMatchShields,
  bracketMatchHasBothTeams,
  bracketSideLabel,
  bracketSideShield,
  buildBracketMatchUpdatePayload,
} from './mesaUtils';

const API = appConstants.API_URL;

function clampGoals(n) {
  const x = Number(n);
  if (Number.isNaN(x)) return 0;
  return Math.max(0, Math.min(99, x));
}

export default function MesaBracketPartidoPage() {
  const { tournamentId, matchId } = useParams();
  const [match, setMatch] = useState(null);
  const [localGoals, setLocalGoals] = useState(0);
  const [foreignGoals, setForeignGoals] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const writeLockRef = useRef(false);

  useEffect(() => {
    moment.locale('es');
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/api/tournamentmatches/${matchId}/finalmatch`);
      const m = applyBracketMatchShields(res.data, tournamentId);
      setMatch(m);
      setLocalGoals(clampGoals(m.localGoals ?? 0));
      setForeignGoals(clampGoals(m.foreignGoals ?? 0));
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'No se pudo cargar el partido');
      setMatch(null);
    } finally {
      setLoading(false);
    }
  }, [matchId, tournamentId]);

  useEffect(() => {
    load();
  }, [load]);

  const mergeBracketResponse = useCallback((raw, baseMatch) => {
    const merged = {
      ...baseMatch,
      ...raw,
      localTeam: raw.localTeam ?? baseMatch.localTeam,
      foreignTeam: raw.foreignTeam ?? baseMatch.foreignTeam,
      groupPositionLocal: raw.groupPositionLocal ?? baseMatch.groupPositionLocal,
      groupPositionForeign: raw.groupPositionForeign ?? baseMatch.groupPositionForeign,
    };
    const m = applyBracketMatchShields(merged, tournamentId);
    setMatch(m);
    setLocalGoals(clampGoals(m.localGoals ?? 0));
    setForeignGoals(clampGoals(m.foreignGoals ?? 0));
  }, [tournamentId]);

  const persistScores = useCallback(
    async (nextLocal, nextForeign) => {
      if (!match || writeLockRef.current) return;
      const snapshot = match;
      if (!snapshot.isActive || snapshot.completed) return;
      writeLockRef.current = true;
      setSaving(true);
      setError('');
      try {
        const body = buildBracketMatchUpdatePayload(snapshot, {
          localGoals: nextLocal,
          foreignGoals: nextForeign,
          isActive: snapshot.isActive,
          completed: snapshot.completed,
        });
        const res = await axios.put(`${API}/api/tournamentmatches/${matchId}/finalmatch`, body);
        mergeBracketResponse(res.data, snapshot);
      } catch (e) {
        setError(e.response?.data?.message || e.message || 'Error al guardar el gol');
      } finally {
        writeLockRef.current = false;
        setSaving(false);
      }
    },
    [match, matchId, mergeBracketResponse]
  );

  const persist = useCallback(
    async (isActive, completed) => {
      if (!match || writeLockRef.current) return;
      const snapshot = match;
      writeLockRef.current = true;
      setSaving(true);
      setError('');
      try {
        const body = buildBracketMatchUpdatePayload(snapshot, {
          localGoals,
          foreignGoals,
          isActive,
          completed,
        });
        const res = await axios.put(`${API}/api/tournamentmatches/${matchId}/finalmatch`, body);
        mergeBracketResponse(res.data, snapshot);
      } catch (e) {
        setError(e.response?.data?.message || e.message || 'Error al guardar');
      } finally {
        writeLockRef.current = false;
        setSaving(false);
      }
    },
    [match, matchId, localGoals, foreignGoals, mergeBracketResponse]
  );

  const bumpLocal = useCallback(
    (d) => {
      if (!match || writeLockRef.current || !match.isActive || match.completed) return;
      const next = clampGoals(localGoals + d);
      if (next === localGoals) return;
      void persistScores(next, foreignGoals);
    },
    [match, localGoals, foreignGoals, persistScores]
  );

  const bumpForeign = useCallback(
    (d) => {
      if (!match || writeLockRef.current || !match.isActive || match.completed) return;
      const next = clampGoals(foreignGoals + d);
      if (next === foreignGoals) return;
      void persistScores(localGoals, next);
    },
    [match, localGoals, foreignGoals, persistScores]
  );

  const hub = `/tournamentactions/${tournamentId}/mesa`;

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!match) {
    return (
      <Container maxWidth="sm" sx={{ py: 3 }}>
        <Button component={RouterLink} to={hub} startIcon={<ArrowBackIcon />} sx={{ mb: 2, textTransform: 'none' }}>
          Volver a la mesa
        </Button>
        {error && <Alert severity="error">{error}</Alert>}
      </Container>
    );
  }

  if (!bracketMatchHasBothTeams(match)) {
    return (
      <Container maxWidth="sm" sx={{ py: 3 }}>
        <Button component={RouterLink} to={hub} startIcon={<ArrowBackIcon />} sx={{ mb: 2, textTransform: 'none' }}>
          Volver a la mesa
        </Button>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Este partido de fase final aún no tiene los dos equipos definidos (plazas vacías o pendientes de cruces
          anteriores). Vuelve cuando el cuadro esté resuelto para ese encuentro.
        </Alert>
      </Container>
    );
  }

  const t1 = bracketSideLabel(match, 'local');
  const t2 = bracketSideLabel(match, 'foreign');
  const s1 = bracketSideShield(match, 'local');
  const s2 = bracketSideShield(match, 'foreign');
  const pending = !match.isActive && !match.completed;
  const live = match.isActive && !match.completed;
  const done = match.completed;
  const canEditGoals = match.isActive && !match.completed;

  let statusChip = { label: 'Pendiente', color: 'warning' };
  if (live) statusChip = { label: 'En juego', color: 'success' };
  if (done) statusChip = { label: 'Finalizado', color: 'default' };

  return (
    <Container maxWidth="sm" sx={{ py: 2, px: 2, pb: 8 }}>
      <Button
        component={RouterLink}
        to={hub}
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2, textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
      >
        Todos los partidos
      </Button>

      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
        <EmojiEventsIcon sx={{ color: '#c9a227' }} />
        <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 0.5 }}>
          Fase final
        </Typography>
      </Stack>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {match.fieldName ? `${match.fieldName} · ` : ''}
            {moment(match.dayHour).format('ddd D MMM, HH:mm')}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f3531' }}>
            {match.name}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
            {t1} vs {t2}
          </Typography>
        </Box>
        <Chip label={statusChip.label} color={statusChip.color} sx={{ fontWeight: 700 }} />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Stack spacing={2}>
        <BracketScoreRow
          name={t1}
          shield={s1}
          value={localGoals}
          onDelta={bumpLocal}
          disabled={saving || !canEditGoals}
          busy={saving}
        />
        <BracketScoreRow
          name={t2}
          shield={s2}
          value={foreignGoals}
          onDelta={bumpForeign}
          disabled={saving || !canEditGoals}
          busy={saving}
        />
      </Stack>

      <Stack spacing={1.5} sx={{ mt: 3 }}>
        {pending && (
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<PlayArrowIcon />}
            disabled={saving}
            onClick={() => persist(true, false)}
            sx={{
              py: 1.75,
              fontWeight: 800,
              fontSize: '1.05rem',
              borderRadius: 2,
              bgcolor: '#b8860b',
              '&:hover': { bgcolor: '#9a7209' },
            }}
          >
            Empezar partido
          </Button>
        )}

        {live && (
          <Button
            fullWidth
            variant="contained"
            color="error"
            size="large"
            startIcon={<StopIcon />}
            disabled={saving}
            onClick={() => persist(false, true)}
            sx={{ py: 1.75, fontWeight: 800, borderRadius: 2, textTransform: 'none' }}
          >
            Finalizar partido
          </Button>
        )}

        {done && (
          <Button
            fullWidth
            variant="outlined"
            color="warning"
            size="large"
            startIcon={<UndoIcon />}
            disabled={saving}
            onClick={() => persist(true, false)}
            sx={{ py: 1.5, fontWeight: 700, borderRadius: 2, textTransform: 'none' }}
          >
            Reabrir para corregir
          </Button>
        )}
      </Stack>

      {pending && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.5 }}>
          Los goles solo se pueden anotar cuando el partido está abierto. Pulsa «Empezar partido» para activar +/−.
        </Typography>
      )}

      {live && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.5 }}>
          Cada toque en +/− guarda el marcador al instante. Si falla la red, el número no cambia y verás un aviso.
        </Typography>
      )}

      {done && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, lineHeight: 1.5 }}>
          Reabre el partido, corrige con +/− (cada cambio se guarda solo) y finaliza de nuevo cuando esté bien.
        </Typography>
      )}
    </Container>
  );
}

function BracketScoreRow({ name, shield, value, onDelta, disabled, busy }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid rgba(201,162,39,0.35)',
        opacity: busy ? 0.72 : 1,
        transition: 'opacity 0.15s ease',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Avatar src={shield || undefined} variant="rounded" sx={{ width: 52, height: 52 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }} noWrap>
            {name}
          </Typography>
          <Typography
            variant="h3"
            component="div"
            sx={{
              fontWeight: 900,
              color: '#8b6914',
              lineHeight: 1.1,
              my: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {value}
            {busy ? <CircularProgress size={28} thickness={5} sx={{ color: '#8b6914' }} /> : null}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="large"
              disabled={disabled || value <= 0}
              onClick={() => onDelta(-1)}
              sx={{ minWidth: 56, minHeight: 52, fontWeight: 800, fontSize: '1.25rem', borderRadius: 2 }}
            >
              −
            </Button>
            <Button
              variant="contained"
              size="large"
              disabled={disabled || value >= 99}
              onClick={() => onDelta(1)}
              sx={{
                minWidth: 56,
                minHeight: 52,
                fontWeight: 800,
                fontSize: '1.25rem',
                borderRadius: 2,
                bgcolor: '#b8860b',
                '&:hover': { bgcolor: '#9a7209' },
              }}
            >
              +
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}
